import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Lightning,
  ArrowSquareOut,
  TrendUp,
  ShieldCheck,
  Target,
  Brain,
  Warning,
  CaretDown,
  CaretUp,
  Star,
  ArrowsClockwise,
  CircleNotch,
  ChartLine,
  Pulse,
  Clock,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { tradingViewStockUrl, screenerStockUrl } from "../../lib/tradingview";
import StrongestHistoryModal from "./StrongestHistoryModal";
import StockList from "./StockList";

const fmt = (n, d = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d })
    : "—";

const ratingColor = {
  Strong: "text-pos border-pos bg-pos-soft",
  Moderate: "text-mod border-mod bg-mod-soft",
  Weak: "text-neg border-neg bg-neg-soft",
};

const scoreColor = (s) => {
  if (s == null) return "text-muted-fg border-subtle";
  if (s >= 8) return "text-pos border-pos bg-pos-soft";
  if (s >= 6) return "text-mod border-mod bg-mod-soft";
  return "text-neg border-neg bg-neg-soft";
};

export function StrongestCard({ item, onWatch, watched }) {
  const [open, setOpen] = useState(false);
  const sym = (item.symbol || "").replace(".NS", "");

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="border border-subtle bg-surface rounded-sm p-5 hover:border-pos/40 transition relative"
      data-testid={`strongest-card-${sym}`}
    >
      {/* Rank badge */}
      <div
        className="absolute -top-2 -left-2 w-7 h-7 rounded-full flex items-center justify-center font-mono text-xs font-bold border-2"
        style={{
          background: "hsl(var(--bg))",
          borderColor: "hsl(var(--signal-positive))",
          color: "hsl(var(--signal-positive))",
        }}
        title={`Rank #${item.rank}`}
        data-testid={`strongest-rank-${sym}`}
      >
        {item.rank}
      </div>

      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <a
              href={tradingViewStockUrl(item.symbol)}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-lg font-bold text-primary-fg hover:text-pos flex items-center gap-1"
              data-testid={`strongest-tv-${sym}`}
            >
              {sym} <ArrowSquareOut size={12} weight="bold" />
            </a>
            <span
              className={
                "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                scoreColor(item.momentum_score)
              }
              data-testid={`strongest-score-${sym}`}
              title={`Composite score: ${item.momentum_score}/10 — Hard gates: Close>EMA20>EMA50, RSI≥55, RS vs Nifty≥0%, ₹5 Cr+ liquidity. Soft scoring: EMA stack, MACD, volume, ADX, 52w proximity, base, candle, drawdown, sector, delivery, fundamentals.`}
            >
              <Lightning size={9} weight="fill" className="inline mr-0.5" />
              {item.momentum_score}/10
            </span>
            {item.fundamental_rating && (
              <span
                className={
                  "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                  (ratingColor[item.fundamental_rating] || "text-muted-fg border-subtle")
                }
                data-testid={`strongest-fundamental-${sym}`}
                title={`Fundamental: ${item.fundamental_rating}${item.fundamental_score ? " (" + item.fundamental_score + "/10)" : ""}`}
              >
                <Brain size={9} weight="fill" className="inline mr-0.5" />
                {item.fundamental_rating}
              </span>
            )}
          </div>
          <div className="text-sm text-muted-fg truncate">
            {item.company_name || sym}
          </div>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          {onWatch && (
            <button
              type="button"
              onClick={() => onWatch({ symbol: item.symbol })}
              data-testid={`strongest-watch-${sym}`}
              title={watched ? "Remove from watchlist" : "Add to watchlist"}
              className={
                "p-1.5 rounded-sm border transition " +
                (watched
                  ? "bg-pos-soft border-pos text-pos hover:bg-pos-soft"
                  : "border-subtle text-muted-fg hover:text-pos hover:border-pos/40")
              }
            >
              <Star size={13} weight={watched ? "fill" : "regular"} />
            </button>
          )}
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
              Current
            </div>
            <div className="font-mono text-lg text-primary-fg tabular-nums">
              ₹{fmt(item.last_price)}
            </div>
            {typeof item.pct_change_today === "number" && (
              <div
                className={
                  "text-[10px] font-mono tabular-nums " +
                  (item.pct_change_today >= 0 ? "text-pos" : "text-neg")
                }
                data-testid={`strongest-pct-${sym}`}
                title="Today's % change vs prior close"
              >
                {item.pct_change_today >= 0 ? "+" : ""}
                {fmt(item.pct_change_today, 2)}% today
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Momentum indicators strip */}
      <div className="grid grid-cols-5 gap-2 mb-3 px-3 py-2 bg-app border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest">
        <div className="flex flex-col gap-0.5" title="Relative Strength Index (14)">
          <div className="text-muted-fg flex items-center gap-1"><Pulse size={9} /> RSI</div>
          <span className="text-pos tabular-nums normal-case">{fmt(item.rsi, 1)}</span>
        </div>
        <div className="flex flex-col gap-0.5" title="ADX(14): trend strength. >25 = strong, rising = accelerating">
          <div className="text-muted-fg flex items-center gap-1"><ChartLine size={9} /> ADX</div>
          <span className={(item.adx >= 35 ? "text-pos" : item.adx >= 25 ? "text-mod" : "text-secondary-fg") + " tabular-nums normal-case"}>
            {item.adx != null ? fmt(item.adx, 1) : "—"}
          </span>
        </div>
        <div className="flex flex-col gap-0.5" title="MACD histogram (line - signal)">
          <div className="text-muted-fg flex items-center gap-1"><ChartLine size={9} /> MACD</div>
          <span className={(item.macd_hist > 0 ? "text-pos" : "text-neg") + " tabular-nums normal-case"}>
            {item.macd_hist >= 0 ? "+" : ""}{fmt(item.macd_hist, 2)}
          </span>
        </div>
        <div className="flex flex-col gap-0.5" title="Today's volume multiple of 20-day avg">
          <div className="text-muted-fg flex items-center gap-1"><TrendUp size={9} /> VOL</div>
          <span className={(item.volume_multiple >= 3 ? "text-pos" : "text-mod") + " tabular-nums normal-case"}>
            {fmt(item.volume_multiple, 1)}×
          </span>
        </div>
        <div className="flex flex-col gap-0.5" title="Above EMA200 confirms long-term institutional uptrend">
          <div className="text-muted-fg">EMA200</div>
          <span className="text-secondary-fg tabular-nums normal-case">₹{fmt(item.ema200 ?? item.ema50)}</span>
        </div>
      </div>

      {/* Premium criteria badges */}
      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
        {item.fresh_breakout && (
          <span
            className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm text-pos border-pos bg-pos-soft"
            title="New 52w high in last 20 bars + close within 5% of high"
            data-testid={`strongest-breakout-${sym}`}
          >
            FRESH 52W BO
          </span>
        )}
        {item.base_range_pct != null && item.base_range_pct < 15 && (
          <span
            className={
              "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
              (item.base_range_pct < 8 ? "text-pos border-pos bg-pos-soft" : "text-mod border-mod bg-mod-soft")
            }
            title={`Pre-breakout consolidation range: ${fmt(item.base_range_pct, 1)}% (lower = tighter base)`}
          >
            TIGHT BASE {fmt(item.base_range_pct, 0)}%
          </span>
        )}
        {item.candle_close_pct != null && item.candle_close_pct >= 70 && (
          <span
            className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm text-pos border-pos bg-pos-soft"
            title={`Closed in upper ${fmt(item.candle_close_pct, 0)}% of day's range, upper wick ${fmt(item.candle_upper_wick_pct, 0)}%`}
          >
            STRONG CLOSE
          </span>
        )}
        {item.deliv_pct != null && item.deliv_pct > 0 && (
          <span
            className={
              "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
              (item.deliv_pct >= 60 ? "text-pos border-pos bg-pos-soft"
                : item.deliv_pct >= 40 ? "text-mod border-mod bg-mod-soft"
                : "text-secondary-fg border-subtle")
            }
            title="Delivery % from NSE bhavcopy — higher = genuine smart-money buying, lower = intraday speculation"
            data-testid={`strongest-deliv-${sym}`}
          >
            DELIV {fmt(item.deliv_pct, 0)}%
          </span>
        )}
        {item.max_drawdown_30d_pct != null && item.max_drawdown_30d_pct <= 12 && (
          <span
            className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm text-secondary-fg border-subtle"
            title="Shallow pullback = institutional holding"
          >
            DD {fmt(item.max_drawdown_30d_pct, 0)}%
          </span>
        )}
      </div>

      {/* Deep links — matches Cup & Handle tab placement (before trade plan) */}
      <div className="flex items-center gap-2 mb-3">
        <a
          href={tradingViewStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`strongest-tv-btn-${sym}`}
        >
          TV ↗
        </a>
        <a
          href={screenerStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`strongest-scr-${sym}`}
        >
          SCR ↗
        </a>
      </div>

      {/* Trade plan — matches Cup & Handle tab layout */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          Trade plan
          {item.risk_reward && (
            <span className="text-secondary-fg ml-1.5">
              · R:R 1:{fmt(item.risk_reward, 1)}
            </span>
          )}
        </div>
        <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
          SL anchored to 10-EMA
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-pos mb-1">
            <TrendUp size={9} weight="bold" /> Entry
          </div>
          <div className="text-primary-fg font-mono text-sm tabular-nums">
            ₹{fmt(item.entry)}
          </div>
        </div>
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-neg mb-1">
            <ShieldCheck size={9} weight="bold" /> SL
          </div>
          <div className="text-neg font-mono text-sm tabular-nums">
            ₹{fmt(item.stop_loss)}
          </div>
          <div className="text-[8px] font-mono uppercase tracking-widest text-muted-fg mt-0.5">
            10-EMA
          </div>
        </div>
        <div className="bg-app border border-subtle rounded-sm p-2.5">
          <div className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-pos mb-1">
            <Target size={9} weight="bold" /> Target
          </div>
          <div className="text-pos font-mono text-sm tabular-nums">
            ₹{fmt(item.target)}
          </div>
        </div>
      </div>

      {/* Weekly 20-EMA replacement guard hint */}
      {item.weekly_ema20 && (
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2">
          <span>
            Weekly 20-EMA: <span className="text-secondary-fg">₹{fmt(item.weekly_ema20)}</span>
          </span>
          <span title="Stock auto-replaced when weekly close drops below 20-EMA">
            Replacement guard <ShieldCheck size={9} weight="bold" className="inline ml-0.5 text-pos" />
          </span>
        </div>
      )}

      {/* Premium momentum filters: RS vs Nifty + Sector RS + 52w-high */}
      {(item.rs_vs_nifty_pct != null || item.pct_below_52w_high != null || item.sector_index) && (
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2 flex-wrap gap-2">
          {item.rs_vs_nifty_pct != null && (
            <span title="Relative Strength: stock 3m return − Nifty 3m return">
              RS vs Nifty:{" "}
              <span className={item.rs_vs_nifty_pct >= 15 ? "text-pos" : item.rs_vs_nifty_pct >= 5 ? "text-mod" : "text-secondary-fg"}>
                {item.rs_vs_nifty_pct >= 0 ? "+" : ""}{fmt(item.rs_vs_nifty_pct, 1)}%
              </span>
            </span>
          )}
          {item.sector_index && item.sector_rs_vs_nifty_pct != null && (
            <span
              title={`Sector ${item.sector_index} 3m return: ${fmt(item.sector_3m_pct, 1)}% (vs Nifty: ${item.sector_rs_vs_nifty_pct >= 0 ? "+" : ""}${fmt(item.sector_rs_vs_nifty_pct, 1)}%)`}
              data-testid={`strongest-sector-${sym}`}
            >
              Sector{" "}
              <span className="text-secondary-fg normal-case">
                {(item.sector_index || "").replace("^CNX", "").replace("^NSE", "").replace("BANK", "Bank")}
              </span>
              :{" "}
              <span className={item.sector_rs_vs_nifty_pct >= 5 ? "text-pos" : item.sector_rs_vs_nifty_pct >= 0 ? "text-mod" : "text-secondary-fg"}>
                {item.sector_rs_vs_nifty_pct >= 0 ? "+" : ""}{fmt(item.sector_rs_vs_nifty_pct, 1)}%
              </span>
            </span>
          )}
          {item.pct_below_52w_high != null && (
            <span title="How far below the 52-week high the current price is">
              From 52w high:{" "}
              <span className={item.pct_below_52w_high <= 5 ? "text-pos" : item.pct_below_52w_high <= 15 ? "text-mod" : "text-secondary-fg"}>
                -{fmt(item.pct_below_52w_high, 1)}%
              </span>
            </span>
          )}
        </div>
      )}

      {/* Fundamental analysis (collapsible) */}
      {item.fundamental_summary ? (
        <div className="mt-3 pt-3 border-t border-subtle">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={`strongest-fund-toggle-${sym}`}
            className="flex items-center justify-between w-full text-left text-xs hover:text-primary-fg"
          >
            <span className="font-mono uppercase tracking-widest text-[10px] text-secondary-fg flex items-center gap-1">
              <Brain size={11} weight="bold" className="text-pos" /> Fundamentals (AI)
            </span>
            {open ? <CaretUp size={11} /> : <CaretDown size={11} />}
          </button>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.2 }}
              className="mt-2 space-y-2"
            >
              <p className="text-xs text-secondary-fg leading-relaxed">
                {item.fundamental_summary}
              </p>
              {item.fundamental_bullish?.length > 0 && (
                <ul className="space-y-1">
                  {item.fundamental_bullish.slice(0, 3).map((b, i) => (
                    <li key={`bull-${i}-${b.slice(0, 24)}`} className="text-[11px] text-secondary-fg flex gap-1.5">
                      <span className="text-pos shrink-0">+</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {item.fundamental_bearish?.length > 0 && (
                <ul className="space-y-1">
                  {item.fundamental_bearish.slice(0, 2).map((b, i) => (
                    <li key={`bear-${i}-${b.slice(0, 24)}`} className="text-[11px] text-secondary-fg flex gap-1.5">
                      <span className="text-neg shrink-0">−</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          )}
        </div>
      ) : (
        <div className="mt-3 pt-3 border-t border-subtle">
          <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            <Warning size={11} className="text-mod" /> Fundamentals analysis pending
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default function StrongestView({ onWatch, watchedSet, onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rsiFloor, setRsiFloor] = useState(0); // 0 = show all, 70/75 = stricter
  const [diversified, setDiversified] = useState(false);

  const load = useCallback(async () => {
    try {
      setErr("");
      const { data } = await api.get(
        diversified ? "/strongest?diversified=true" : "/strongest",
      );
      setData(data);
    } catch (e) {
      console.error("/strongest load", e);
      setErr(e?.response?.data?.detail || "Failed to load strongest stocks");
      setData({ items: [], count: 0 });
    } finally {
      setLoading(false);
    }
  }, [diversified]);

  useEffect(() => {
    setLoading(true);
    load();
    // Auto-refresh every 60s while tab is open
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await api.post("/admin/strongest/scan");
      await load();
    } catch (e) {
      console.error("strongest scan failed", e);
      const detail = e?.response?.data?.detail || e?.message || "Scan failed";
      // eslint-disable-next-line no-alert
      alert(detail);
    } finally {
      setScanning(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center text-muted-fg py-16" data-testid="strongest-loading">
        Loading strongest stocks…
      </div>
    );
  }

  const items = data?.items || [];
  const filtered = rsiFloor > 0
    ? items.filter((it) => (it.rsi ?? 0) >= rsiFloor)
    : items;

  return (
    <div data-testid="strongest-view">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Lightning size={14} weight="fill" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
              Strongest Stocks · Top 10
            </div>
            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-mod text-mod bg-mod-soft rounded-sm">
              Admin Only
            </span>
            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-pos/40 text-pos bg-pos-soft rounded-sm">
              Auto-refresh 60s
            </span>
          </div>
          <p className="text-xs text-muted-fg max-w-3xl leading-relaxed">
            Top 10 momentum picks ranked by composite score. Hard gates:
            Close &gt; EMA20 &gt; EMA50, RSI ≥ 55, RS vs Nifty ≥ 0%, ₹5 Cr+ liquidity.
            Soft scoring: EMA stack quality, MACD, volume surge, ADX, 52w-high
            proximity, fresh breakout, tight base, strong candle, drawdown,
            sector strength, delivery %, fundamentals. Auto-replaced on weekly
            close below 20-EMA.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 flex-wrap">
          {/* Diversified mode toggle — pulls top-30 pool with max 2 per sector */}
          <button
            type="button"
            onClick={() => setDiversified((d) => !d)}
            data-testid="strongest-diversified-toggle"
            title={diversified
              ? "Showing diversified top-10 (max 2 per sector). Click to revert to pure score-rank."
              : "Click to apply max-2-per-sector cap from the top-30 pool — better portfolio balance."}
            className={
              "px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm border transition " +
              (diversified
                ? "border-pos bg-pos-soft text-pos"
                : "border-subtle text-muted-fg hover:text-primary-fg hover:border-pos/40")
            }
          >
            {diversified ? "Diversified ✓" : "Diversify"}
          </button>
          {/* RSI floor filter — clientside, no backend rescan */}
          <div className="inline-flex border border-subtle rounded-sm p-0.5 bg-surface" title="Show only picks with RSI ≥ N (client-side filter)">
            {[
              { v: 0, label: "All" },
              { v: 70, label: "≥70" },
              { v: 75, label: "≥75" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setRsiFloor(opt.v)}
                data-testid={`strongest-rsi-filter-${opt.v}`}
                className={
                  "px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm transition " +
                  (rsiFloor === opt.v
                    ? "bg-pos-soft text-pos"
                    : "text-muted-fg hover:text-primary-fg")
                }
              >
                RSI {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            data-testid="strongest-history-btn"
            className="px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition flex items-center gap-1.5"
            title="Replacement history — last 30 days of dropped picks + P&L"
          >
            <Clock size={11} weight="bold" /> History
          </button>
          <button
            type="button"
            onClick={triggerScan}
            disabled={scanning}
            data-testid="strongest-rescan-btn"
            className="px-3 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition flex items-center gap-1.5 disabled:opacity-50"
            title="Trigger a full-universe re-scan now"
          >
            {scanning ? (
              <><CircleNotch size={11} weight="bold" className="animate-spin" /> Scanning…</>
            ) : (
              <><ArrowsClockwise size={11} weight="bold" /> Rescan</>
            )}
          </button>
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            {filtered.length} of {items.length}
          </div>
        </div>
      </div>

      {err && (
        <div className="mb-4 px-3 py-2 border border-neg bg-neg-soft text-neg text-xs font-mono rounded-sm" data-testid="strongest-err">
          {err}
        </div>
      )}

      {items.length === 0 ? (
        <div
          className="p-10 border border-subtle rounded-sm bg-surface text-center"
          data-testid="strongest-empty"
        >
          <Lightning size={24} className="mx-auto text-muted-fg mb-3" />
          <div className="text-primary-fg font-medium mb-1">
            No qualifying stocks today
          </div>
          <div className="text-sm text-muted-fg max-w-md mx-auto">
            All 10 institutional-grade filters must pass: 4-stack EMA (20/50/200),
            RSI 65–80, MACD &gt; 0, ADX &gt; 25 rising, today's volume ≥ 2× avg,
            fresh 52w breakout, tight base, strong green candle, RS vs Nifty ≥ 5%.
            <br />
            In a weak market, very few stocks qualify — try <b>Rescan</b> on
            stronger up-days or wait for next 16:30 IST scheduled scan.
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="p-8 border border-subtle rounded-sm bg-surface text-center"
          data-testid="strongest-filter-empty"
        >
          <div className="text-secondary-fg text-sm">
            No picks match RSI ≥ {rsiFloor}. Try a lower threshold or clear the filter.
          </div>
        </div>
      ) : (
        <>
          {items.length < 10 && (
            <div className="mb-4 px-3 py-2 border border-mod/40 bg-mod-soft/30 text-mod text-xs font-mono rounded-sm flex items-center gap-2" data-testid="strongest-limited-info">
              <Warning size={12} weight="bold" />
              Showing {items.length} of max 10 — only stocks meeting ALL strict
              institutional criteria are listed. Quality &gt; quantity.
            </div>
          )}
          <StockList
            items={filtered}
            onOpen={(it) => onOpenStock?.({ ...it, strongest_mode: true })}
            onWatch={onWatch}
            watchedSet={watchedSet}
            testIdPrefix="strongest-row"
            testid="strongest-list"
            rowProps={(it) => ({
              iconKind: "strongest",
              rank: it.rank,
              scoreKey: "momentum_score",
              scoreLabel: "Score",
              rightHint: it.fresh_breakout ? "FRESH 52W BO" : undefined,
            })}
          />
        </>
      )}

      <StrongestHistoryModal open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </div>
  );
}
